import { flushSync } from 'react-dom'

import { themeManager } from '@/Ship/Stores/ThemeManager'

import type { ThemeMode } from '@/Ship/Stores/ThemeManager'

/** Скільки триває розкриття нової теми колом, мс. */
const REVEAL_MS = 600

/**
 * Скільки тримається клас рівного переливу там, де браузер не вміє View
 * Transitions, мс: сам перелив — 0.3 с (`theme.css`), поява іконки — 0.5 с.
 */
const FADE_MS = 600

/** На `<html>` на час перемикання: власні переходи елементів вимкнено. */
const SWITCHING_CLASS = 'wl-theme-switching'

/** На `<html>` на час розкриття колом: стилі знімків і поява нової іконки. */
const REVEAL_CLASS = 'wl-theme-reveal'

/** На `<html>` на час рівного переливу (запасний шлях). */
const FADE_CLASS = 'wl-theme-fade'

/** Останнє розкриття: класи знімає лише воно, а не те, яке перебив швидкий повторний клік. */
let currentReveal: ViewTransition | null = null

/** Центр кнопки — звідти розходиться коло. Без кнопки — центр екрана. */
const originOf = (element?: Element | null): { x: number; y: number } => {
    const rect = element?.getBoundingClientRect()

    return rect
        ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
        : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
}

/** Застосувати тему одразу: React домальовує зміни до повернення з функції. */
const applyNow = (mode: ThemeMode): void => {
    flushSync(() => themeManager.setMode(mode))
}

/**
 * Перемкнути тему з анімацією.
 *
 * Без неї кожен елемент перефарбовувався у власному темпі: у кого в CSS стоїть
 * `transition`, той перетікав 0.2–0.3 с за своєю кривою, решта мінялася
 * миттєво, — і інтерфейс «переливався врізнобій».
 *
 * Тепер на мить перемикання власні переходи елементів вимкнено, а анімує
 * браузер увесь екран разом (View Transitions API): знімок старої теми
 * лишається, нова розкривається колом від кнопки. Де API немає, усі кольори
 * перетікають одночасно й однаково. Тим, хто попросив систему не анімувати
 * (`prefers-reduced-motion`), — миттєве перемикання без переливів.
 */
export const switchTheme = (origin?: Element | null): void => {
    const root = document.documentElement
    const next: ThemeMode = themeManager.isDark ? 'light' : 'dark'

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        root.classList.add(SWITCHING_CLASS)
        applyNow(next)

        // Стилі перераховуються ще з вимкненими переходами — тоді вмикати їх уже безпечно
        void getComputedStyle(root).color
        requestAnimationFrame(() => root.classList.remove(SWITCHING_CLASS))

        return
    }

    if (typeof document.startViewTransition !== 'function') {
        root.classList.add(FADE_CLASS)
        themeManager.setMode(next)
        window.setTimeout(() => root.classList.remove(FADE_CLASS), FADE_MS)

        return
    }

    const { x, y } = originOf(origin)

    // До найдальшого кута екрана — коло має накрити все
    const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))

    root.classList.add(SWITCHING_CLASS, REVEAL_CLASS)

    const transition = document.startViewTransition(() => applyNow(next))
    currentReveal = transition

    transition.ready
        .then(() => {
            root.animate(
                { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
                {
                    duration: REVEAL_MS,
                    // Швидкий старт: коло одразу рушає від кнопки, сповільнюється на краях
                    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                    pseudoElement: '::view-transition-new(root)',
                }
            )
        })
        // Перебите новим кліком розкриття відхиляє `ready` — тема при цьому вже застосована
        .catch(() => undefined)

    const cleanup = (): void => {
        if (currentReveal !== transition) return

        currentReveal = null
        root.classList.remove(SWITCHING_CLASS, REVEAL_CLASS)
    }

    transition.finished.then(cleanup, cleanup)
}
